import type { AssetMetadata } from '../cloud/contracts';
import type { WebKilnProject, ProjectAsset } from '../types';

export type StorageCapability = 'metadata' | 'binary' | 'transformations';
export interface AssetStorageAdapter {
  readonly kind: 'local' | 'metadata-only' | 'r2-future';
  capabilities(): ReadonlySet<StorageCapability>;
  list(project: WebKilnProject): AssetMetadata[];
  save(project: WebKilnProject, asset: AssetMetadata): WebKilnProject;
  remove(project: WebKilnProject, assetId: string): WebKilnProject;
}

export interface AssetFilterOptions {
  searchQuery?: string;
  folder?: string;
  tag?: string;
  brandGroup?: string;
  type?: 'all' | 'image' | 'document' | 'vector' | 'video' | 'audio' | 'other' | string;
}

export type AssetSortField = 'name' | 'size' | 'date' | 'usage';
export type AssetSortDirection = 'asc' | 'desc';

const localAsset = (asset: ProjectAsset): AssetMetadata => ({
  id: asset.id,
  workspaceId: 'local',
  siteId: 'local-site',
  filename: asset.name,
  mimeType: asset.mime,
  size: asset.size,
  altText: asset.altText ?? '',
  caption: asset.caption ?? '',
  folder: asset.folder && asset.folder.startsWith('/') ? asset.folder : (asset.folder ? `/${asset.folder}` : '/'),
  tags: asset.tags ?? [],
  focalPoint: asset.focalPoint ?? { x: 50, y: 50 },
  width: asset.width ?? null,
  height: asset.height ?? null,
  contentHash: asset.contentHash ?? null,
  brandGroup: asset.brandGroup ?? null,
  usageCount: asset.usageCount ?? 0,
  storageStatus: asset.storageStatus ?? 'local',
  transformations: { available: false, reason: 'Local binary processing is not configured' },
  createdAt: asset.createdAt ?? new Date(0).toISOString(),
  updatedAt: asset.updatedAt ?? new Date(0).toISOString(),
});

export class LocalAssetStorageAdapter implements AssetStorageAdapter {
  readonly kind = 'local' as const;
  capabilities(): ReadonlySet<StorageCapability> {
    return new Set(['metadata', 'binary']);
  }
  list(project: WebKilnProject): AssetMetadata[] {
    return project.assets.map(localAsset);
  }
  save(project: WebKilnProject, asset: AssetMetadata): WebKilnProject {
    const next = project.assets.filter((item) => item.id !== asset.id);
    next.push({
      id: asset.id,
      name: asset.filename,
      mime: asset.mimeType,
      size: asset.size,
      altText: asset.altText,
      caption: asset.caption,
      folder: asset.folder,
      tags: asset.tags,
      focalPoint: asset.focalPoint,
      width: asset.width,
      height: asset.height,
      contentHash: asset.contentHash,
      brandGroup: asset.brandGroup,
      usageCount: asset.usageCount,
      storageStatus: asset.storageStatus,
      createdAt: asset.createdAt,
      updatedAt: asset.updatedAt,
    });
    return { ...project, assets: next };
  }
  remove(project: WebKilnProject, assetId: string): WebKilnProject {
    return { ...project, assets: project.assets.filter((item) => item.id !== assetId) };
  }
}

export class MetadataOnlyAssetStorageAdapter implements AssetStorageAdapter {
  readonly kind = 'metadata-only' as const;
  capabilities(): ReadonlySet<StorageCapability> {
    return new Set(['metadata']);
  }
  list(): AssetMetadata[] {
    return [];
  }
  save(project: WebKilnProject): WebKilnProject {
    return project;
  }
  remove(project: WebKilnProject): WebKilnProject {
    return project;
  }
}

export class FutureR2AssetStorageAdapter implements AssetStorageAdapter {
  readonly kind = 'r2-future' as const;
  capabilities(): ReadonlySet<StorageCapability> {
    return new Set();
  }
  list(): AssetMetadata[] {
    return [];
  }
  save(project: WebKilnProject): WebKilnProject {
    return project;
  }
  remove(project: WebKilnProject): WebKilnProject {
    return project;
  }
}

export function selectAssetStorageAdapter(cloudConfigured: boolean): AssetStorageAdapter {
  return cloudConfigured ? new MetadataOnlyAssetStorageAdapter() : new LocalAssetStorageAdapter();
}

export function storageState(adapter: AssetStorageAdapter): {
  configured: boolean;
  label: string;
  detail: string;
} {
  if (adapter.kind === 'metadata-only')
    return {
      configured: true,
      label: 'Metadata only',
      detail: 'Binary storage is not configured. Asset details are safe to edit.',
    };
  if (adapter.kind === 'r2-future')
    return {
      configured: false,
      label: 'Storage not configured',
      detail: 'R2 is reserved for a future approved milestone.',
    };
  return {
    configured: true,
    label: 'Local storage',
    detail: 'Local asset metadata remains available in this browser.',
  };
}

export function canDeleteAsset(asset: Pick<AssetMetadata, 'usageCount'>): boolean {
  return asset.usageCount === 0;
}
export function findDuplicateAssetIds(
  assets: Pick<AssetMetadata, 'id' | 'contentHash'>[],
): Set<string> {
  const groups = new Map<string, string[]>();
  assets.forEach((asset) => {
    if (!asset.contentHash) return;
    groups.set(asset.contentHash, [...(groups.get(asset.contentHash) ?? []), asset.id]);
  });
  return new Set([...groups.values()].filter((ids) => ids.length > 1).flat());
}
export function estimateStorageUsage(assets: Pick<AssetMetadata, 'size'>[]): number {
  return assets.reduce((total, asset) => total + Math.max(0, asset.size), 0);
}

export function normalizeFolderPath(folder?: string): string {
  if (!folder || folder.trim() === '' || folder.trim() === '/') return '/';
  const trimmed = folder.trim().replace(/\\/g, '/');
  const withLeading = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const withoutTrailing = withLeading.replace(/\/+$/, '');
  return withoutTrailing || '/';
}

export function extractFolderHierarchy(assets: Array<Pick<AssetMetadata, 'folder'>>): string[] {
  const folders = new Set<string>(['/']);
  for (const asset of assets) {
    const norm = normalizeFolderPath(asset.folder);
    folders.add(norm);
    const segments = norm.split('/').filter(Boolean);
    let accum = '';
    for (const seg of segments) {
      accum += `/${seg}`;
      folders.add(accum);
    }
  }
  return Array.from(folders).sort((a, b) => {
    if (a === '/') return -1;
    if (b === '/') return 1;
    return a.localeCompare(b);
  });
}

export function matchesAssetType(mimeType: string, typeFilter?: string): boolean {
  if (!typeFilter || typeFilter === 'all') return true;
  const mime = mimeType.toLowerCase();
  switch (typeFilter) {
    case 'image':
      return mime.startsWith('image/') && mime !== 'image/svg+xml';
    case 'vector':
      return mime === 'image/svg+xml';
    case 'document':
      return (
        mime.startsWith('text/') ||
        mime.includes('pdf') ||
        mime.includes('document') ||
        mime.includes('sheet') ||
        mime.includes('presentation') ||
        mime === 'application/json' ||
        mime === 'application/zip'
      );
    case 'video':
      return mime.startsWith('video/');
    case 'audio':
      return mime.startsWith('audio/');
    case 'other':
      return (
        !mime.startsWith('image/') &&
        !mime.startsWith('video/') &&
        !mime.startsWith('audio/') &&
        !mime.startsWith('text/') &&
        !mime.includes('pdf')
      );
    default:
      return mime === typeFilter.toLowerCase();
  }
}

export function filterAssets(assets: AssetMetadata[], options?: AssetFilterOptions): AssetMetadata[] {
  if (!options) return assets;
  const query = options.searchQuery?.trim().toLowerCase() ?? '';
  const folder = options.folder ? normalizeFolderPath(options.folder) : null;
  const tag = options.tag?.trim().toLowerCase();
  const brand = options.brandGroup?.trim().toLowerCase();
  const type = options.type;

  return assets.filter((asset) => {
    if (folder !== null && folder !== undefined) {
      const assetFolder = normalizeFolderPath(asset.folder);
      if (assetFolder !== folder) return false;
    }
    if (type && !matchesAssetType(asset.mimeType, type)) {
      return false;
    }
    if (tag && !asset.tags.some((t) => t.toLowerCase() === tag)) {
      return false;
    }
    if (brand && (asset.brandGroup ?? '').toLowerCase() !== brand) {
      return false;
    }
    if (query) {
      const matchName = asset.filename.toLowerCase().includes(query);
      const matchAlt = asset.altText.toLowerCase().includes(query);
      const matchCap = asset.caption.toLowerCase().includes(query);
      const matchFolder = asset.folder.toLowerCase().includes(query);
      const matchTags = asset.tags.some((t) => t.toLowerCase().includes(query));
      const matchBrand = (asset.brandGroup ?? '').toLowerCase().includes(query);
      if (!matchName && !matchAlt && !matchCap && !matchFolder && !matchTags && !matchBrand) {
        return false;
      }
    }
    return true;
  });
}

export function sortAssets(
  assets: AssetMetadata[],
  sortBy: AssetSortField = 'name',
  order: AssetSortDirection = 'asc',
): AssetMetadata[] {
  const mult = order === 'desc' ? -1 : 1;
  return [...assets].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return mult * a.filename.localeCompare(b.filename);
      case 'size':
        return mult * (a.size - b.size);
      case 'usage':
        return mult * (a.usageCount - b.usageCount);
      case 'date': {
        const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return mult * (da - db);
      }
      default:
        return 0;
    }
  });
}

export function calculateAssetUsage(
  project: WebKilnProject,
  assetId: string,
  assetFilename?: string,
): number {
  if (!project.pages || !project.pages.length) return 0;
  let count = 0;
  const targetId = assetId.toLowerCase();
  const rawFilename = assetFilename ? assetFilename.toLowerCase() : '';
  const baseName = rawFilename ? rawFilename.replace(/\.[^.]+$/, '').trim() : '';
  const altName = baseName ? baseName.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim() : '';
  const slugName = baseName ? baseName.replace(/[\s_]+/g, '-').trim() : '';

  function inspectValue(val: unknown): void {
    if (val === null || val === undefined) return;
    if (typeof val === 'string') {
      const lower = val.toLowerCase();
      if (lower === targetId || lower.includes(targetId)) {
        count++;
      } else if (rawFilename && rawFilename.length > 3 && lower.includes(rawFilename)) {
        count++;
      } else if (altName && altName.length >= 4 && lower.includes(altName)) {
        count++;
      } else if (slugName && slugName.length >= 4 && lower.includes(slugName)) {
        count++;
      } else if (baseName && baseName.length >= 4 && lower.includes(baseName)) {
        count++;
      }
    } else if (Array.isArray(val)) {
      for (const item of val) inspectValue(item);
    } else if (typeof val === 'object') {
      for (const key of Object.keys(val)) {
        inspectValue((val as Record<string, unknown>)[key]);
      }
    }
  }

  for (const page of project.pages) {
    if (page.projectData) {
      inspectValue(page.projectData);
    }
  }
  return count;
}

export function replaceAssetInProject(
  project: WebKilnProject,
  fromAssetId: string,
  toAssetId: string,
  toAssetFilename?: string,
): { project: WebKilnProject; replacedCount: number } {
  let replacedCount = 0;

  function replaceInValue(val: unknown): unknown {
    if (typeof val === 'string') {
      if (val === fromAssetId) {
        replacedCount++;
        return toAssetId;
      }
      if (toAssetFilename && val.includes(fromAssetId)) {
        replacedCount++;
        return val.replaceAll(fromAssetId, toAssetId);
      }
      return val;
    }
    if (Array.isArray(val)) {
      return val.map((item) => replaceInValue(item));
    }
    if (val && typeof val === 'object') {
      const result: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(val)) {
        result[k] = replaceInValue(v);
      }
      return result;
    }
    return val;
  }

  const updatedPages = project.pages.map((p) => {
    if (!p.projectData) return p;
    return {
      ...p,
      projectData: replaceInValue(p.projectData),
    };
  });

  const updatedAssets = project.assets.map((a) => {
    if (a.id === fromAssetId) {
      return { ...a, usageCount: 0 };
    }
    if (a.id === toAssetId) {
      return { ...a, usageCount: (a.usageCount ?? 0) + replacedCount };
    }
    return a;
  });

  return {
    project: {
      ...project,
      pages: updatedPages,
      assets: updatedAssets,
    },
    replacedCount,
  };
}

export function computeAssetContentHash(filename: string, size: number, mimeType: string): string {
  const str = `${filename}:${size}:${mimeType}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return `hash-${Math.abs(hash).toString(16)}`;
}


export function updateAssetReferencesInProjectData(
  pageData: Record<string, unknown>,
  targetAsset: {
    id: string;
    filename?: string;
    altText?: string;
  },
  updates: {
    replacementId?: string;
    replacementFilename?: string;
    replacementAlt?: string;
    replacementUrl?: string;
    focalPoint?: { x: number; y: number };
  },
): { updated: boolean; count: number } {
  if (!pageData || typeof pageData !== 'object') return { updated: false, count: 0 };

  const targetId = targetAsset.id.toLowerCase();
  const rawFilename = (targetAsset.filename || '').toLowerCase();
  const baseName = rawFilename ? rawFilename.replace(/\.[^.]+$/, '').trim() : '';
  const altName = baseName ? baseName.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim() : '';
  const targetAlt = (targetAsset.altText || '').toLowerCase().trim();

  const matchedComponentIds = new Set<string>();
  let count = 0;

  function inspectComponent(comp: Record<string, unknown>): void {
    if (!comp || typeof comp !== 'object') return;
    const attrs = ((comp.attributes as Record<string, unknown>) || {});
    const dataAssetId = String(attrs['data-asset-id'] || '').toLowerCase();
    const src = String(attrs.src || '').toLowerCase();
    const alt = String(attrs.alt || '').toLowerCase().trim();

    const isMatch =
      dataAssetId === targetId ||
      (targetId && src.includes(targetId)) ||
      (rawFilename && rawFilename.length > 3 && src.includes(rawFilename)) ||
      (altName && altName.length >= 4 && alt === altName) ||
      (targetAlt && targetAlt.length >= 3 && alt === targetAlt) ||
      (baseName && baseName.length >= 4 && alt === baseName);

    if (isMatch) {
      count++;
      if (attrs.id) {
        matchedComponentIds.add('#' + String(attrs.id));
      }
      if (updates.replacementId) {
        attrs['data-asset-id'] = updates.replacementId;
      }
      if (updates.replacementUrl) {
        attrs.src = updates.replacementUrl;
      } else if (updates.replacementFilename && typeof attrs.src === 'string' && rawFilename) {
        attrs.src = (attrs.src as string).replaceAll(rawFilename, updates.replacementFilename);
      }
      if (updates.replacementAlt !== undefined) {
        attrs.alt = updates.replacementAlt;
      }
      if (updates.focalPoint) {
        const posVal = updates.focalPoint.x + '% ' + updates.focalPoint.y + '%';
        const compStyle = ((comp.style as Record<string, unknown>) || {});
        compStyle['object-position'] = posVal;
        comp.style = compStyle;
        const styleStr = typeof attrs.style === 'string' ? attrs.style : '';
        const styleObj: Record<string, string> = {};
        if (styleStr) {
          for (const pair of styleStr.split(';')) {
            const [k, v] = pair.split(':').map((s) => s.trim());
            if (k && v) styleObj[k] = v;
          }
        }
        styleObj['object-position'] = posVal;
        attrs.style = Object.entries(styleObj).map(([k, v]) => `${k}:${v}`).join(';');
      }
    }

    if (Array.isArray(comp.components)) {
      for (const child of comp.components) {
        if (child && typeof child === 'object') {
          inspectComponent(child as Record<string, unknown>);
        }
      }
    }
  }

  if (Array.isArray(pageData.components)) {
    for (const child of pageData.components) {
      if (child && typeof child === 'object') {
        inspectComponent(child as Record<string, unknown>);
      }
    }
  } else if (pageData.components && typeof pageData.components === 'object') {
    inspectComponent(pageData.components as Record<string, unknown>);
  }

  if (Array.isArray(pageData.pages)) {
    for (const p of pageData.pages) {
      if (p && typeof p === 'object' && Array.isArray((p as Record<string, unknown>).frames)) {
        for (const frame of (p as Record<string, unknown>).frames as Record<string, unknown>[]) {
          if (frame && typeof frame === 'object' && frame.component) {
            inspectComponent(frame.component as Record<string, unknown>);
          }
        }
      }
    }
  }

  if (updates.focalPoint && Array.isArray(pageData.styles)) {
    const posStr = updates.focalPoint.x + '% ' + updates.focalPoint.y + '%';
    for (const rule of pageData.styles as Record<string, unknown>[]) {
      if (!rule || typeof rule !== 'object') continue;
      const selectors = Array.isArray(rule.selectors)
        ? rule.selectors.map((s) => (typeof s === 'string' ? s : (s as { name?: string })?.name ? '#' + (s as { name?: string }).name : ''))
        : [];
      const hasMatch = selectors.some((s) => matchedComponentIds.has(s) || [...matchedComponentIds].some((m) => s.includes(m)));
      if (hasMatch) {
        const ruleStyle = ((rule.style as Record<string, unknown>) || {});
        ruleStyle['object-position'] = posStr;
        rule.style = ruleStyle;
      }
    }
  }

  return { updated: count > 0, count };
}
